import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalDialog, ModalClose, Typography, Stack, Button, Box } from '@mui/joy';
import { useCreateMealPlanEntryMutation, useGetMealPlanEntriesQuery } from '@/features/meals/meal-plan/api';
import { MealTypePicker } from './MealTypePicker';
import { localDateToString, getStartOfWeek } from '@/utils/datetime';
import { MonthlyCalendarHeader } from '@/components/calendar/MonthlyCalendarHeader';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useGetHouseholdQuery } from '@/features/households/api';

export function AddToMealPlanModal({ open, onClose, recipe, householdId }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));    
  const { data: householdData } = useGetHouseholdQuery({ householdId });
  const weekStartDay = householdData?.data?.week_start_day ?? 1;

  const getDefaultMealType = (recipe) => {
    if (!recipe?.categories) return 'dinner';
    const mealTypes = ['breakfast', 'lunch', 'dinner'];
    for (const mealType of mealTypes) {
      if (recipe.categories.includes(mealType)) {
        return mealType;
      }
    }
    return 'dinner';
  };

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState(() => getDefaultMealType(recipe));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [createMutation, { isLoading: isCreating }] = useCreateMealPlanEntryMutation();
  const { data: mealPlanEntries } = useGetMealPlanEntriesQuery({ householdId });

  useEffect(() => {
    if (open && recipe) {
      setSelectedMealType(getDefaultMealType(recipe));
    }
  }, [open, recipe]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentWeekStart = useMemo(() => getStartOfWeek(new Date(), weekStartDay), [weekStartDay]);

  const monthStart = useMemo(() => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    return start;
  }, [currentMonth]);

  const monthEnd = useMemo(() => {
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [currentMonth]);

  const calendarDays = useMemo(() => {
    const days = [];
    const firstDayOfMonth = monthStart.getDay();
    const startDate = new Date(monthStart);
    let offset = firstDayOfMonth - weekStartDay;
    if (offset < 0) offset += 7;
    startDate.setDate(startDate.getDate() - offset);
    const baseDate = startDate.getDate();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(baseDate + i);
      days.push(date);
    }
    return days;
  }, [monthStart, weekStartDay]);

  const mealCountsByDate = useMemo(() => {
    const counts = {};
    if (mealPlanEntries?.data) {
      mealPlanEntries.data.forEach(entry => {
        const entryDate = localDateToString(new Date(entry.date));
        counts[entryDate] = (counts[entryDate] || 0) + 1;
      });
    }
    return counts;
  }, [mealPlanEntries]);

  const isDateInCurrentWeek = (date) => {
    const weekStart = getStartOfWeek(date, weekStartDay);
    return weekStart.getTime() === currentWeekStart.getTime();
  };

  const isDateInMonth = (date) => {
    return date >= monthStart && date <= monthEnd;
  };

  const isDateInPast = (date) => {
    return date < today;
  };

  const handleDateClick = (date) => {
    if (isDateInPast(date)) return;
    setSelectedDate(date);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !recipe) return;

    try {
      await createMutation({
        householdId,
        data: {
          date: localDateToString(selectedDate),
          recipe_id: recipe.id,
          meal_type: selectedMealType,
        },
      });
      const weekStart = getStartOfWeek(selectedDate, weekStartDay);
      const weekString = localDateToString(weekStart);
      onClose();
      setSelectedDate(null);
      navigate(`/h/${householdId}/meals/plan?week=${weekString}`);
    } catch (error) {
      console.error('Failed to add recipe to meal plan:', error);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setSelectedMealType(getDefaultMealType(recipe));
    onClose();
  };
  
  const weekDaysLabels = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const orderedDays = [];
    for (let i = 0; i < 7; i++) {
      orderedDays.push(days[(weekStartDay + i) % 7]);
    }
    return orderedDays;
  }, [weekStartDay]);

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Add to Meal Plan
        </Typography>

        <Stack gap={2}>          
          <MonthlyCalendarHeader currentMonth={currentMonth} onMonthChange={setCurrentMonth} />          

          <Box>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  {weekDaysLabels.map(day => (
                    <th key={day} style={{ padding: '8px', textAlign: 'center', width: `${100 / 7}%` }}>
                      <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                        {day}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 6 }).map((_, weekIndex) => (
                  <tr key={weekIndex}>
                    {weekDaysLabels.map((_, dayIndex) => {
                      const dateIndex = weekIndex * 7 + dayIndex;
                      const date = calendarDays[dateIndex];
                      if (!date) return <td key={dayIndex} style={{ padding: isMobile ? '3px' : '4px' }}></td>;

                      const dateStr = localDateToString(date);
                      const mealCount = mealCountsByDate[dateStr] || 0;
                      const isCurrentWeek = isDateInCurrentWeek(date);
                      const isInMonth = isDateInMonth(date);
                      const isPast = isDateInPast(date);
                      const isSelected = selectedDate && dateStr === localDateToString(selectedDate);
                      const isToday = dateStr === localDateToString(today);

                      return (
                        <td
                          key={dayIndex}
                          style={{
                            padding: isMobile ? '3px' : '4px',
                            position: 'relative',
                            width: `${100 / 7}%`,
                          }}
                        >
                          <Box
                            onClick={() => !isPast && handleDateClick(date)}
                            sx={{
                              width: '100%',
                              aspectRatio: '1',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 'sm',
                              cursor: isPast ? 'default' : 'pointer',
                              pointerEvents: isPast ? 'none' : 'auto',
                              backgroundColor: isSelected
                                ? 'var(--joy-palette-primary-500)'
                                : isCurrentWeek
                                ? 'var(--joy-palette-primary-200)'
                                : !isPast
                                ? 'var(--joy-palette-neutral-100)'
                                : 'transparent',
                              border: isToday
                                ? '2px solid var(--joy-palette-primary-500)'
                                : isSelected
                                ? '2px solid var(--joy-palette-primary-700)'
                                : '2px solid transparent',
                              opacity: isInMonth ? (isPast ? 0.5 : 1) : 0.3,
                              position: 'relative',
                              '&:hover': !isPast && {
                                backgroundColor: isSelected
                                  ? 'var(--joy-palette-primary-600)'
                                  : isCurrentWeek
                                  ? 'var(--joy-palette-primary-300)'
                                  : 'var(--joy-palette-primary-100)',
                              },
                            }}
                          >
                            {mealCount > 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: { xs: '-6px', sm: '2px' },
                                  right: { xs: '-6px', sm: '2px' },
                                  backgroundColor: isSelected
                                    ? 'var(--joy-palette-primary-700)'
                                    : 'var(--joy-palette-primary-500)',
                                  color: isSelected ? 'white' : 'white',
                                  borderRadius: { xs: '8px', sm: '10px' },
                                  minWidth: { xs: '15px', sm: '18px' },
                                  height: { xs: '15px', sm: '18px' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: { xs: 0.25, sm: 0.5 },
                                  fontSize: { xs: '9px', sm: '10px' },
                                  fontWeight: 600,
                                  lineHeight: 1,
                                }}
                              >
                                {mealCount}
                              </Box>
                            )}
                            <Typography
                              level="body-sm"
                              sx={{
                                fontWeight: isToday ? 700 : isSelected ? 600 : 400,
                                color: isSelected ? 'white' : 'inherit',
                              }}
                            >
                              {date.getDate()}
                            </Typography>
                          </Box>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          {selectedDate && (
            <Stack gap={2}>
              <Box>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Selected: {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </Typography>
                <MealTypePicker value={selectedMealType} onChange={setSelectedMealType} />
              </Box>
              <Button onClick={handleSubmit} loading={isCreating} fullWidth startDecorator={<AddIcon />}>Add to Meal Plan</Button>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

